import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import {
  paymentCreateSchema,
  type CustomerRow,
  type PaginatedResponse,
  type PaymentCreateInput,
} from '@runik/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePicker } from '../ui/date-picker';
import { toast } from '../ui/sonner';
import { ApiError, request } from '../../lib/auth/client';
import { createPayment } from '../../lib/queries/payments';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PaymentFormDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<Date | undefined>();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PaymentCreateInput>({
    resolver: zodResolver(paymentCreateSchema),
    defaultValues: { currency: 'USD' },
  });

  const customers = useQuery({
    queryKey: ['customers', 'all-for-select'],
    queryFn: () => request<PaginatedResponse<CustomerRow>>('/api/customers?limit=100'),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setServerError(null);
      setDueDate(undefined);
      reset({ currency: 'USD' });
    }
  }, [open, reset]);

  useEffect(() => {
    if (dueDate) {
      setValue('dueDate', dueDate.toISOString(), { shouldValidate: true });
    }
  }, [dueDate, setValue]);

  const create = useMutation({
    mutationFn: (data: PaymentCreateInput) => createPayment(data),
    onSuccess: () => {
      toast.success('Ödeme oluşturuldu');
      qc.invalidateQueries({ queryKey: ['payments'] });
      onOpenChange(false);
    },
  });

  const onSubmit = async (data: PaymentCreateInput) => {
    setServerError(null);
    try {
      await create.mutateAsync({
        ...data,
        amount: Number(data.amount),
      });
    } catch (err) {
      setServerError(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Hata oluştu');
    }
  };

  const customerId = watch('customerId');
  const currency = watch('currency');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Yeni Ödeme</DialogTitle>
          <DialogDescription>Fatura numarası boş bırakılırsa otomatik üretilir.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {serverError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label>Müşteri *</Label>
            <Select value={customerId} onValueChange={(v) => setValue('customerId', v, { shouldValidate: true })}>
              <SelectTrigger>
                <SelectValue placeholder="Müşteri seçin" />
              </SelectTrigger>
              <SelectContent>
                {(customers.data?.data ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.companyName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.customerId && <p className="text-sm text-rose-600">{errors.customerId.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Tutar *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                {...register('amount', { valueAsNumber: true })}
              />
              {errors.amount && <p className="text-sm text-rose-600">{errors.amount.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>Para Birimi</Label>
              <Select
                value={currency}
                onValueChange={(v) => setValue('currency', v as 'TRY' | 'USD' | 'EUR')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="TRY">TRY</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Vade Tarihi *</Label>
            <DatePicker value={dueDate} onChange={setDueDate} />
            {errors.dueDate && <p className="text-sm text-rose-600">{errors.dueDate.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Açıklama</Label>
            <Input id="description" {...register('description')} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="invoiceNo">Fatura No (opsiyonel)</Label>
            <Input id="invoiceNo" placeholder="Otomatik: RUNIK-2026-NNNN" {...register('invoiceNo')} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              İptal
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? <><Spinner /> Kaydediliyor…</> : 'Oluştur'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
